import { useLocation } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { useDemoGuideStore } from "./useDemoGuideStore";
// Rule lives in eligibility.ts so it is testable without pulling in the app.
import { isDemoGuideEligible } from "./eligibility";
import { useGuideViewport } from "@/shared/hooks/useGuideViewport";
import { hasPriorAuth } from "@/utils/priorAuth";

export { isDemoGuideEligible };

/**
 * Dev/testing override — NON-PRODUCTION BUILDS ONLY.
 *
 *   ?demo-guide=force  — show the guide even when signed in
 *   ?demo-guide=reset  — wipe local state, then re-run from the picker
 *   ?demo-guide=off    — stop overriding
 *
 * Captured into sessionStorage at module load for the same reason
 * src/onboarding/useOnboardingGate.ts does it: the query string does not
 * survive to render time. Keycloak's redirect returns to a fixed URI with the
 * query stripped, and the tab system rewrites the URL to ?tab=...
 */
export type DemoGuideDevOverride = "force" | "reset" | null;

const DEV_OVERRIDE_KEY = "demoGuide.devOverride";
const isProduction = import.meta.env.VITE_APP_ENV === "production";

if (!isProduction) {
  try {
    const value = new URLSearchParams(window.location.search).get("demo-guide");
    if (value === "force" || value === "reset") {
      sessionStorage.setItem(DEV_OVERRIDE_KEY, value);
    } else if (value === "off") {
      sessionStorage.removeItem(DEV_OVERRIDE_KEY);
    }
  } catch {
    // sessionStorage unavailable — override simply stays off
  }
}

export function getDemoGuideDevOverride(): DemoGuideDevOverride {
  if (isProduction) return null;
  try {
    const value = sessionStorage.getItem(DEV_OVERRIDE_KEY);
    return value === "force" || value === "reset" ? value : null;
  } catch {
    return null;
  }
}

export function setDemoGuideDevOverride(value: "force" | "off"): void {
  try {
    if (value === "off") sessionStorage.removeItem(DEV_OVERRIDE_KEY);
    else sessionStorage.setItem(DEV_OVERRIDE_KEY, value);
  } catch {
    // ignore
  }
}

export type DemoGuidePhase =
  /** Not a logged-out visitor, or not known to be one yet — show nothing. */
  | "ineligible"
  /** First arrival: ask which asset class they trade. */
  | "picker"
  /** An asset class is picked and the run is live: show the rail. */
  | "run"
  /** Run closed or declined: checklist only. */
  | "idle";

export function useDemoGuideGate(): {
  phase: DemoGuidePhase;
  showChecklist: boolean;
} {
  const { authenticated, keycloakInstance, keycloakInitFailed } = useAuth();
  const state = useDemoGuideStore((s) => s.state);
  const justCompleted = useDemoGuideStore((s) => s.justCompleted);
  const { pathname } = useLocation();

  /**
   * The login page is for signing in, nothing else. The chooser sits on top
   * of the login button there, so the guide stays out of it entirely —
   * including under the dev override.
   */
  const onLoginPage = pathname === "/login";

  /**
   * Keycloak has answered, either way.
   *
   * initKeycloak writes `keycloakInstance` alongside the real `authenticated`
   * on success, and `keycloakInitFailed` on failure. Before either lands, the
   * store's `authenticated: false` is a default rather than a fact — see
   * isDemoGuideEligible.
   */
  const authResolved = !!keycloakInstance || keycloakInitFailed;

  // Hoisted: inlining this into the `||` below would skip the hook whenever
  // the dev override is on, which is a conditional hook call.
  const viewportSupported = useGuideViewport();

  const devForce = getDemoGuideDevOverride() !== null;
  const eligible =
    !onLoginPage &&
    (devForce ||
      isDemoGuideEligible({
        authResolved,
        authenticated,
        viewportSupported,
        hasPriorAuth: hasPriorAuth(),
      }));

  let phase: DemoGuidePhase;
  if (!eligible) {
    phase = "ineligible";
  } else if (!state?.pickerAnsweredAt) {
    phase = "picker";
  } else if (
    state.assetClass &&
    !state.exitedAt &&
    // A finished run keeps the rail only for the load that finished it. On a
    // later visit `completedAt` is still set but justCompleted is not, so the
    // checklist takes over rather than the price panel reappearing. See the
    // flag's note in useDemoGuideStore.
    (!state.completedAt || justCompleted)
  ) {
    phase = "run";
  } else {
    phase = "idle";
  }

  const showChecklist =
    (phase === "idle" || phase === "run") && !state?.checklistDismissedAt;

  return { phase, showChecklist };
}
